/**
 * Qué se borra y qué se conserva al traer históricos.
 *
 * Hasta ahora el pull hacía `db.invoices.clear()` —la tabla entera— y volvía a
 * llenarla con lo que había pedido. Eso tiene dos consecuencias que nadie ve
 * hasta que muerden:
 *
 * 1. **Se borran documentos que el terminal todavía le debe a Business
 *    Central.** Una factura con `pendingSync`, a medio enviar o en borrador
 *    desaparece de la base local, y con ella el `Id_TPV` que el guardia de
 *    idempotencia usa para reconocerla. Al reintentar, BC recibe un documento
 *    que no puede emparejar con el anterior: **duplicado**.
 * 2. **Se borra fuera del alcance de lo pedido.** El pull filtra por tienda,
 *    caja y rango de fechas, pero borraba todo: traer una semana de una caja se
 *    llevaba por delante los documentos de las demás.
 *
 * Aquí se decide el reemplazo sin tocar la base, para poder comprobarlo: la
 * función devuelve qué borrar y qué insertar, y quien la llama lo aplica.
 *
 * Acuerdo `contrato-traer-historicos.md`, ronda 3.
 */

/**
 * La clave con la que dos filas son «el mismo documento».
 *
 * En este orden a propósito: `Id_TPV` es el único estable desde el alta local
 * —el `No` no existe hasta que Business Central lo asigna—, y `Pre_Assigned_No`
 * es el último recurso para un documento enviado pero aún sin registrar.
 */
export const claveDelDocumento = (doc) =>
  String(doc?.Id_TPV || doc?.No || doc?.Pre_Assigned_No || "").trim();

/**
 * Si la fila local guarda algo que Business Central todavía no tiene.
 *
 * Son las que sostienen el guardia de idempotencia. Borrarlas no pierde
 * historia: pierde el hilo de una venta a medio enviar.
 */
export const esDeudaConBC = (fila) =>
  Boolean(fila?.pendingSync) || Boolean(fila?.syncing) || Boolean(fila?.isDraft);

/** Si la fila cae dentro de lo que se acaba de pedir a Business Central. */
export const entraEnElAlcance = (fila, { caja, desde, hasta, turno } = {}) => {
  if (caja && String(fila?.caja ?? "") !== String(caja)) return false;

  const fecha = String(fila?.Posting_Date ?? "").slice(0, 10);
  if (desde && (!fecha || fecha < desde)) return false;
  if (hasta && (!fecha || fecha > hasta)) return false;

  // El turno es el único filtro opcional del pull; sin él, no acota nada.
  if (turno !== undefined && turno !== null && turno !== "") {
    if (String(fila?.turno ?? "") !== String(turno)) return false;
  }

  return true;
};

/**
 * El plan de reemplazo, sin tocar la base.
 *
 * @param {object} args
 * @param {Array} args.existentes  Filas que ya hay en la base local.
 * @param {Array} args.entrantes   Documentos que devolvió Business Central.
 * @param {object} args.alcance    `{ caja, desde, hasta, turno }`, el mismo
 *   filtro con el que se pidieron.
 * @returns {{ aBorrar: Array<number>, aInsertar: Array<object>, conservadas: Array<object> }}
 */
export const planDeReemplazo = ({ existentes = [], entrantes = [], alcance = {} } = {}) => {
  const conservadas = [];
  const aBorrar = [];

  for (const fila of existentes) {
    // Fuera del alcance no se toca: traer una caja no puede borrar las demás.
    if (!entraEnElAlcance(fila, alcance)) {
      conservadas.push(fila);
      continue;
    }
    // Dentro del alcance, lo que el terminal aún le debe a BC se queda.
    if (esDeudaConBC(fila)) {
      conservadas.push(fila);
      continue;
    }
    if (fila?.id !== undefined && fila?.id !== null) aBorrar.push(fila.id);
  }

  // Un documento conservado no se vuelve a insertar: sería el mismo dos veces,
  // y la copia de BC pisaría el estado de envío de la local.
  const yaEstan = new Set(conservadas.map(claveDelDocumento).filter(Boolean));
  const aInsertar = entrantes.filter((doc) => {
    const clave = claveDelDocumento(doc);
    return !clave || !yaEstan.has(clave);
  });

  return { aBorrar, aInsertar, conservadas };
};

/**
 * Los documentos cuyas líneas y pagos hay que retirar.
 *
 * Se borran los de los documentos borrados y **sólo esos**: las líneas de una
 * venta conservada tienen que seguir ahí, o la fila queda sin detalle.
 */
export const numerosDeLosBorrados = (existentes = [], aBorrar = []) => {
  const ids = new Set(aBorrar);
  const numeros = new Set();
  for (const fila of existentes) {
    if (!ids.has(fila?.id)) continue;
    for (const n of [fila?.No, fila?.Pre_Assigned_No]) {
      if (n) numeros.add(String(n));
    }
  }
  return numeros;
};
